// Лента «Изменения» протокола (015: contracts/api.md, «ProtocolRevisions»; research, Р3): одна
// запись на принятую команду — вид, поля, число пунктов, автор, время. Хранится навсегда
// (`minutes.revisions`, только добавление), в отличие от записей команд (30 дней).
import { z } from '../kernel/zod.ts';
import { DateSchema, IdSchema, PersonRefSchema, TimestampSchema } from '../kernel/common.ts';
import { commandKinds } from './command.ts';
import { ConflictFieldSchema } from './conflict.ts';
import { ItemEventKindSchema } from './history.ts';
import { partyFields } from './item.ts';

export const REVISIONS_PAGE = 100;

/**
 * Вид команды ленты: команды таблицы, перевод, возврат в режим файла и перенос 027
 * (`system.parties`, автор — SYSTEM_ACTOR_ID).
 */
export const RevisionCommandKindSchema = z.enum([
  ...commandKinds,
  'transfer',
  'revert',
  'system.parties',
  // 030 (research Р7, FR-045): ответственный пункта по нынешней связи упоминания — автор тот,
  // кто принял решение в справочнике.
  'system.relink',
]);
export type RevisionCommandKind = z.infer<typeof RevisionCommandKindSchema>;

export const RevisionsQuerySchema = z.strictObject({
  before: z.coerce.number().int().min(2).optional(),
  limit: z.coerce.number().int().min(1).max(REVISIONS_PAGE).optional(),
  /** Только записи после этой ревизии (016: «Только после снимка»). */
  after: z.coerce.number().int().min(0).optional(),
  /** 030 (FR-041): только записи с ответом ответственного — «Ответы ответственных». */
  answers: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});

export const ProtocolRevisionSchema = z.object({
  revision: z.number().int().min(1),
  /** null — ревизия до 015, вид команды не сохранился (подпись строится по видам событий). */
  commandKind: RevisionCommandKindSchema.nullable(),
  eventKinds: z.array(ItemEventKindSchema),
  fields: z.array(ConflictFieldSchema),
  itemCount: z.number().int().min(0),
  firstItem: z.object({ id: IdSchema, number: z.string(), title: z.string() }).nullable(),
  /** У записи фиксации недели — дата зафиксированной недели (016), у остальных — нет. */
  weekDate: DateSchema.nullable().optional(),
  /**
   * 028 (FR-028): правка «Ответственного» и «Контрагента» одного пункта — названия до и после
   * через «; », как в ячейке; лента показывает разницу «+ … · − …». У прочих записей — нет.
   */
  listChanges: z
    .array(
      z.object({
        field: z.enum(partyFields),
        before: z.string().nullable(),
        after: z.string().nullable(),
      }),
    )
    .optional(),
  /** 029: у восстановления и возврата — версия (ревизия и время её правки). */
  version: z.object({ revision: z.number().int().min(1), at: TimestampSchema }).optional(),
  /** 030 (FR-041): у записи — ответ ответственного из «Моих задач». */
  answer: z.boolean().optional(),
  actor: PersonRefSchema,
  at: TimestampSchema,
});
export type ProtocolRevision = z.infer<typeof ProtocolRevisionSchema>;

export const ProtocolRevisionsSchema = z.object({
  items: z.array(ProtocolRevisionSchema).max(REVISIONS_PAGE),
  /** Курсор `before` следующей страницы; null — записей больше нет. */
  next: z.number().int().min(2).nullable(),
  /** Обзор изменений после последнего снимка (016: CORE-08) — в ответе без курсора. */
  sinceSnapshot: z
    .object({
      snapshotId: IdSchema,
      weekDate: DateSchema,
      revisions: z.number().int().min(0),
      items: z.number().int().min(0),
    })
    .nullable()
    .optional(),
});
export type ProtocolRevisions = z.infer<typeof ProtocolRevisionsSchema>;
